import React, { useState, useEffect, useCallback } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { analyticsService, PlatformStats, UserGrowthData, BookingTrendData, ServiceTypePopularity, GeographicDistribution } from '../services/analyticsService';
import './AnalyticsPage.css';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

export const AnalyticsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [platformStats, setPlatformStats] = useState<PlatformStats | null>(null);
  const [userGrowth, setUserGrowth] = useState<UserGrowthData[]>([]);
  const [bookingTrends, setBookingTrends] = useState<BookingTrendData[]>([]);
  const [servicePopularity, setServicePopularity] = useState<ServiceTypePopularity[]>([]);
  const [geographicData, setGeographicData] = useState<GeographicDistribution[]>([]);
  const [trendDays, setTrendDays] = useState(30);
  const [trendPeriod, setTrendPeriod] = useState<'daily' | 'weekly' | 'monthly'>('daily');

  const loadAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [stats, growth, trends, popularity, geo] = await Promise.all([
        analyticsService.getPlatformStats(),
        analyticsService.getUserGrowthTrends(trendDays),
        analyticsService.getBookingTrends(trendDays, trendPeriod),
        analyticsService.getServiceTypePopularity(),
        analyticsService.getGeographicDistribution(),
      ]);

      setPlatformStats(stats);
      setUserGrowth(growth);
      setBookingTrends(trends);
      setServicePopularity(popularity);
      setGeographicData(geo.slice(0, 10));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics');
      console.error('Error loading analytics:', err);
    } finally {
      setLoading(false);
    }
  }, [trendDays, trendPeriod]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-IN').format(num);
  };

  if (loading) {
    return <div className="analytics-page"><div className="loading">Loading analytics...</div></div>;
  }

  if (error) {
    return <div className="analytics-page"><div className="error-message">{error}</div></div>;
  }

  return (
    <div className="analytics-page">
      <div className="analytics-header">
        <h1>Platform Analytics</h1>
        <div className="analytics-controls">
          <select value={trendDays} onChange={(e) => setTrendDays(Number(e.target.value))}>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 180 days</option>
          </select>
          <select value={trendPeriod} onChange={(e) => setTrendPeriod(e.target.value as 'daily' | 'weekly' | 'monthly')}>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
          <button onClick={loadAnalytics}>Refresh</button>
        </div>
      </div>

      {platformStats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">Total Users</div>
            <div className="stat-value">{formatNumber(platformStats.totalUsers)}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Total Providers</div>
            <div className="stat-value">{formatNumber(platformStats.totalProviders)}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Total Bookings</div>
            <div className="stat-value">{formatNumber(platformStats.totalBookings)}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Total Revenue</div>
            <div className="stat-value">{formatCurrency(platformStats.totalRevenue)}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Active Bookings</div>
            <div className="stat-value">{formatNumber(platformStats.activeBookings)}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Completed Bookings</div>
            <div className="stat-value">{formatNumber(platformStats.completedBookings)}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Cancelled Bookings</div>
            <div className="stat-value">{formatNumber(platformStats.cancelledBookings)}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Total Payments</div>
            <div className="stat-value">{formatNumber(platformStats.totalPayments)}</div>
          </div>
        </div>
      )}

      <div className="charts-grid">
        {userGrowth.length > 0 && (
          <div className="chart-card">
            <h2>User Growth Trends</h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={userGrowth}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="count" stroke="#8884d8" name="New Users" />
                <Line type="monotone" dataKey="cumulative" stroke="#82ca9d" name="Total Users" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {bookingTrends.length > 0 && (
          <div className="chart-card">
            <h2>Booking Trends</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={bookingTrends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="bookings" fill="#8884d8" name="Total Bookings" />
                <Bar dataKey="completed" fill="#82ca9d" name="Completed" />
                <Bar dataKey="cancelled" fill="#ff8042" name="Cancelled" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {bookingTrends.length > 0 && (
          <div className="chart-card">
            <h2>Revenue Trends</h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={bookingTrends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip formatter={(value: number) => formatCurrency(value)} />
                <Legend />
                <Line type="monotone" dataKey="revenue" stroke="#0088FE" name="Revenue" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {servicePopularity.length > 0 && (
          <div className="chart-card">
            <h2>Service Type Popularity</h2>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={servicePopularity}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ serviceType, percent }) => `${serviceType} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {servicePopularity.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {geographicData.length > 0 && (
          <div className="chart-card">
            <h2>Top Locations (by Bookings)</h2>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={geographicData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="city" type="category" width={100} />
                <Tooltip />
                <Legend />
                <Bar dataKey="bookingCount" fill="#8884d8" name="Bookings" />
                <Bar dataKey="userCount" fill="#82ca9d" name="Users" />
                <Bar dataKey="providerCount" fill="#ff8042" name="Providers" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};
